import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import AuthService from '../features/auth/services/AuthService'
import { setAccessToken, clearAccessToken } from '../lib/ApiClient'

export const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider')
  }
  return context
}

// Construye el objeto de usuario a partir de la respuesta del servidor
const buildUser = (data) => {
  if (!data?.userId && !data?.username) return null
  return {
    id: data.userId || data.id,
    username: data.username,
    email: data.email,
    role: (data.roles?.[0] || '').replace('ROLE_', '') || 'user',
  }
}

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient()

  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [initialLoad, setInitialLoad] = useState(true)
  const [tokenVerified, setTokenVerified] = useState(false)

  const verificationInProgress = useRef(false)
  const isMounted = useRef(true)
  // Incrementa en cada login/logout para invalidar loadUser() en vuelo
  const authVersion = useRef(0)

  // ── Login ──────────────────────────────────────────────────────────────────
  const loginMutation = useMutation({
    mutationFn: ({ email, password }) => AuthService.login(email, password),
    onSuccess: (data) => {
      authVersion.current++
      if (data.accessToken) setAccessToken(data.accessToken)
      setUser(buildUser(data))
      setTokenVerified(true)
      setError(null)
    },
    onError: (err) => {
      setError(err.message || 'Error de autenticación')
      throw err
    },
  })

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logoutMutation = useMutation({
    mutationFn: () => AuthService.logout(),
    onSettled: () => {
      authVersion.current++
      clearAccessToken()
      // queryClient.clear() se ejecuta en el useEffect de abajo, DESPUÉS de
      // que React confirme user=null. Llamarlo aquí (antes del setState) provoca
      // que React Query relance queries con isAuthenticated todavía true.
      setUser(null)
      setTokenVerified(false)
    },
  })

  const handleLogout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync()
    } catch (err) {
      console.error('Error en logout:', err)
      clearAccessToken()
      setUser(null)
      setTokenVerified(false)
    }
  }, [logoutMutation])

  // ── Carga inicial — verifica la cookie con el servidor ─────────────────────
  const loadUser = useCallback(async () => {
    if (!isMounted.current || verificationInProgress.current) {
      setInitialLoad(false)
      setLoading(false)
      return
    }

    verificationInProgress.current = true
    setLoading(true)
    setError(null)

    // Captura la versión antes de la llamada async.
    // Si un login/logout ocurre mientras esperamos, descartamos el resultado.
    const versionSnapshot = authVersion.current

    try {
      const { isValid, userData } = await AuthService.verifyToken()
      if (isMounted.current && authVersion.current === versionSnapshot) {
        if (isValid && userData) {
          if (userData.accessToken) setAccessToken(userData.accessToken)
          setUser(buildUser(userData))
          setTokenVerified(true)
        } else {
          setUser(null)
          setTokenVerified(false)
        }
      }
    } catch (err) {
      console.error('Error cargando usuario:', err)
      if (isMounted.current && authVersion.current === versionSnapshot) {
        setUser(null)
        setTokenVerified(false)
      }
    } finally {
      verificationInProgress.current = false
      if (isMounted.current) {
        setLoading(false)
        setInitialLoad(false)
      }
    }
  }, [])

  useEffect(() => {
    isMounted.current = true
    loadUser()
    return () => {
      isMounted.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Escucha el evento que dispara ApiClient cuando falla el refresh de token.
  // Limpia el estado sin hard-redirect; AuthenticatedLayout redirigirá a /login.
  useEffect(() => {
    const handleSessionExpired = () => {
      authVersion.current++
      clearAccessToken()
      // Solo limpiamos estado; queryClient.clear() lo gestiona el useEffect de user
      setUser(null)
      setTokenVerified(false)
    }
    window.addEventListener('auth:session-expired', handleSessionExpired)
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired)
    }
  }, [])

  // Limpia el caché de React Query DESPUÉS de que React confirme user=null.
  // Hacerlo síncronamente antes del setState provoca que React Query relance
  // queries mientras isAuthenticated todavía es true → bucle 401/refresh.
  const prevUserRef = useRef(user)
  useEffect(() => {
    const wasLoggedIn = prevUserRef.current !== null
    prevUserRef.current = user
    if (wasLoggedIn && user === null) {
      queryClient.clear()
    }
  }, [user, queryClient])

  // ── API pública ────────────────────────────────────────────────────────────
  const handleLogin = async (email, password) => {
    setLoading(true)
    try {
      await loginMutation.mutateAsync({ email, password })
      return true
    } catch (err) {
      console.error('Error en login:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const combinedLoading =
    loading || loginMutation.isPending || logoutMutation.isPending

  const contextValue = {
    user,
    loading: combinedLoading,
    error: error || loginMutation.error?.message,
    initialLoad,
    isAuthenticated: !!user && tokenVerified,
    handleLogin,
    handleLogout,
    // rememberMe eliminado — las cookies gestionan la persistencia de sesión
    rememberMe: false,
    setRememberMe: () => {},
    hasRole: (requiredRole) => {
      if (!user?.role) return false
      return Array.isArray(requiredRole)
        ? requiredRole.includes(user.role)
        : user.role === requiredRole
    },
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}
